// @ts-check
// ── The dashboard's bottom row: Needs you this week, and Waiting on you ────────
//
// Clayton 10/18 and 10/19 (_handoffs/ultimate-dashboard.md items 1 and 2; the approved prototype
// mockups/dashboard-needs-you-2026-10-18.html, local only). At the VERY BOTTOM of the dashboard, side by side at half
// width: Needs you on the left, Waiting on you on the right. A card with nothing in it is not drawn and the other takes
// the whole row. Built to scan, not to read: one tag a row, one short muted line, filter chips for the kinds that have
// anyone, quiet icon actions, one toast with Undo after each action, handled names folded into "N handled".
//
// The server (coach-needs-you.js, _shared/dashboard/needs-you.js and waiting-on-you.js) makes every list and every
// word; this file draws them and sends what the coach picks.
//
// ⚠️ ITS OWN CLOCK, like the Today card: the lists load when the dashboard shows (at most once a minute), after every
// choice, after a calendar change, on coming back to the tab, and every 2 minutes while showing (so a message he
// sends from the corner chat moves the name to "handled" without a reload). A period pick never reloads them.
// ⚠️ THE PAGE REDRAWS ITS COLUMN (index.js draw): everything the coach opened or picked lives in S, never only in the
// page, and nyPaint draws it again into the new row.
// ⚠️ NEVER IMPORT THE CALENDAR, THE CHECK-INS PAGE OR THE CORNER CHAT AT THE TOP: each is loaded when it is needed.

import { callFunction } from '../api.js';
import { escHtml, showToast } from '../utils.js';
import { getInitials } from '../inbox/format-helpers.js';
import { photoImgHtml } from '../messages/client-photo.js';
import { ICON } from './tiles.js';
import { shortDay } from './words.js';

const FRESH_MS = 60_000;
const TICK_MS = 120_000;

const svg = (/** @type {string} */ d) => `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${d}</svg>`;
const I = {
  msg: svg('<path d="M21 12a8 8 0 01-11.6 7.1L4 20l1-4.6A8 8 0 1121 12z"/>'),
  done: svg('<path d="M5 12.5l4.5 4.5L19 7.5"/>'),
  more: '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><circle cx="5" cy="12" r="1.8"/><circle cx="12" cy="12" r="1.8"/><circle cx="19" cy="12" r="1.8"/></svg>',
  not: svg('<circle cx="12" cy="12" r="9"/><path d="M5.6 5.6l12.8 12.8"/>'),
  // The CRM's outlined amber triangle (his ruling: never a filled one)
  warn: svg('<path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><path d="M12 9v4M12 17h.01"/>'),
};

const KINDS = /** @type {const} */ ([['quiet', 'Gone quiet'], ['checkin', 'Missed check-in'], ['new', 'Not started'], ['eye', 'Keep an eye on']]);
const TAG_CLASS = { quiet: 'is-quiet', checkin: 'is-ci', new: '', eye: 'is-warn' };
const REASONS = { trip: 'On a trip', sick: 'Sick or injured', outside: 'Talked outside the app', no_checkins: 'Doesn\'t do check-ins', other: 'Other' };
/** @param {string} kind */
const reasonsFor = (kind) => (kind === 'checkin' ? ['no_checkins', 'trip', 'sick', 'other'] : ['trip', 'sick', 'outside', 'other']);

const S = {
  /** @type {any} */ data: null,
  /** @type {Promise<void>|null} */ loading: null,
  loadedAt: 0, failed: false, started: false,
  /** @type {ReturnType<typeof setTimeout>|null} */ tick: null,
  nyFilter: 'all', wFilter: 'all',
  showNoApp: false, showHandled: false, showLaterSessions: false, showLaterPrograms: false,
  /** the row whose ⋯ menu is open @type {string|null} */ menuFor: null,
  /** the row asking "Why?", and the reason waiting for its weeks @type {{ key: string, why: string|null }|null} */ asking: null,
  /** @type {(() => void)|null} */ unlisten: null,
  /** a load was asked for while one ran: one more runs after it */ again: false,
  /** when the page last changed a row itself (a choice): an answer read before then is old */ dirtyAt: 0,
  /** the button to put the keyboard on after the next draw @type {string|null} */ focusNext: null,
  /** Message pressed: the client whose chat is open, until he sends @type {{ key: string, at: string }|null} */ chatFor: null,
  /** @type {(() => void)|null} */ unfeed: null,
};

/** A value inside a quoted attribute selector (today.js's rule). @param {unknown} v */
const q = (v) => String(v == null ? '' : v).replace(/["\\]/g, '\\$&');
/** A Needs you row's key: one client, one kind. @param {any} i */
const keyOf = (i) => `${i.clientId}:${i.choiceKind}`;

function host() { return /** @type {HTMLElement|null} */ (document.querySelector('#dv-root .ny-row')); }
function showing() {
  const v = document.getElementById('view-dashboard');
  return !!v && !v.classList.contains('hidden') && document.visibilityState !== 'hidden';
}

// ── Loading ──

/**
 * Load the lists; draws when they arrive. Never two at once: asked again while one runs, one more runs after it.
 * ⚠️ AN ANSWER READ BEFORE A CHOICE IS OLD (final review 10/20): drawn, it would put back a row he just handled. It is
 * dropped and the lists load again.
 */
function load() {
  if (S.loading) { S.again = true; return S.loading; }
  const asked = Date.now();
  S.loading = (async () => {
    try {
      const body = await callFunction('coach-needs-you', null, 'GET');
      if (!body || !body.needsYou || !body.waiting) throw new Error('an answer without the lists');
      if (S.dirtyAt >= asked) S.again = true;
      else { S.data = body; S.failed = false; S.loadedAt = Date.now(); }
    } catch (err) {
      console.warn('[dashboard] Needs you did not load:', err instanceof Error ? err.message : err);
      if (!S.data) S.failed = true;
    } finally {
      S.loading = null;
      nyPaint();
      if (S.again) { S.again = false; void load(); }
    }
  })();
  return S.loading;
}

function schedule() {
  if (S.tick) clearTimeout(S.tick);
  S.tick = setTimeout(() => { S.tick = null; if (showing()) void load(); schedule(); }, TICK_MS);
}

/** The dashboard is showing: start once, load when out of date, else draw what it has. Cheap. */
export function nyShow() {
  if (/** @type {any} */ (window)._appMember) return; // the dashboard is the owner's only
  if (!S.started) {
    S.started = true;
    document.addEventListener('click', onClick);
    document.addEventListener('keydown', onKey);
    const back = () => { if (showing() && Date.now() - S.loadedAt >= FRESH_MS) void load(); };
    document.addEventListener('visibilitychange', back);
    window.addEventListener('focus', back);
    schedule();
  }
  if (!S.data || Date.now() - S.loadedAt >= FRESH_MS) void load();
  else nyPaint();
}

// ── Drawing ──

/** @param {{ name: string, avatarUrl?: string|null }} p */
const avatar = (p) => `<span class="ny-av" aria-hidden="true">${escHtml(getInitials(p.name))}${photoImgHtml(p.avatarUrl || null, 'ny-av-img')}</span>`;
const first = (/** @type {string} */ n) => String(n || '').split(' ')[0];

/** What was done, and when the name comes back, for the handled list and the toast. @param {any} i */
function handledWords(i) {
  const h = i.handled || {};
  const what = h.action === 'messaged' ? 'messaged' : h.action === 'done' ? 'marked done'
    : `not needed, ${String(/** @type {any} */ (REASONS)[h.reason] || 'other').toLowerCase()}`;
  const less = i.choiceKind === 'less';
  const next = h.action === 'not_needed'
    ? (h.checkBackOn ? `Back on ${shortDay(h.checkBackOn)}.` : i.kind === 'checkin' ? 'Back when they send a check-in and miss again.'
      // Not started stays off until it changes: they sign in (then it asks again), or they do something
      : i.kind === 'new' ? (i.basis === 'never' ? 'Back if they sign in and still don\'t start.' : 'Off the list until they do something in the app.')
        : less ? 'Back in 4 weeks if they are still doing less.' : 'Back if they go quiet again.')
    : i.kind === 'checkin' ? 'Back if they miss their next check-in.' : i.kind === 'new' ? 'Back in a week if they still have not started.'
      : less ? 'Back in a week if they are still doing less.' : 'Back in a week if they are still quiet.';
  return { what, next };
}

/** @param {any} i */
function rowHtml(i) {
  const k = keyOf(i), menu = S.menuFor === k, ask = S.asking && S.asking.key === k ? S.asking : null;
  const reasons = !ask ? '' : ask.why
    ? `<span>Check back in</span>${[1, 2, 3].map((n) => `<button type="button" class="ny-b" data-ny-weeks="${n}">${n} ${n === 1 ? 'week' : 'weeks'}</button>`).join('')}`
    : `<span>Why?</span>${reasonsFor(i.kind).map((r) => `<button type="button" class="ny-b" data-ny-reason="${r}">${escHtml(/** @type {any} */ (REASONS)[r])}</button>`).join('')}`;
  return `<div class="ny-item" data-ny-key="${escHtml(k)}">${avatar(i)}
    <div class="ny-who"><div class="ny-name"><span>${escHtml(i.name)}</span>${i.worried ? `<span class="ny-worry">${I.warn}You're worried</span>` : ''}</div><div class="ny-detail" title="${escHtml(i.detail)}">${escHtml(i.detail)}</div></div>
    <span class="ny-tag ${/** @type {any} */ (TAG_CLASS)[i.kind] || ''}">${escHtml(i.tag)}</span>
    <div class="ny-acts"><button type="button" class="ny-b is-msg" data-ny-act="msg" title="Message ${escHtml(first(i.name))}" aria-label="Message ${escHtml(i.name)}">${I.msg}<span class="lbl">Message</span></button><button type="button" class="ny-b" data-ny-act="done" title="Mark done" aria-label="Mark ${escHtml(i.name)} done">${I.done}<span class="lbl">Done</span></button>
      <button type="button" class="ny-b is-more" data-ny-act="more" aria-label="More for ${escHtml(i.name)}" aria-haspopup="menu" aria-expanded="${menu}">${I.more}</button>
      <div class="ny-menu" role="menu"${menu ? '' : ' hidden'}><button type="button" role="menuitem" data-ny-act="not">${I.not}Not needed…</button><button type="button" role="menuitem" data-ny-act="worry">${I.warn}${i.worried ? 'Not worried anymore' : 'I\'m worried'}</button></div></div>
    <div class="ny-reasons" role="group" aria-label="Why it is not needed">${reasons}</div></div>`;
}

/** @param {any} N @param {boolean} wide */
function needsCard(N, wide) {
  const open = N.items, handled = N.handled;
  const kinds = KINDS.filter(([k]) => open.some((/** @type {any} */ i) => i.kind === k));
  if (S.nyFilter !== 'all' && !kinds.some(([k]) => k === S.nyFilter)) S.nyFilter = 'all';
  const rows = open.filter((/** @type {any} */ i) => S.nyFilter === 'all' || i.kind === S.nyFilter);
  const chips = kinds.length > 1 ? `<div class="ny-chips" role="group" aria-label="Show">${[['all', 'All', open.length], ...kinds.map(([k, l]) => [k, l, open.filter((/** @type {any} */ i) => i.kind === k).length])]
    .map(([k, l, n]) => `<button type="button" class="ny-chip" data-ny-filter="${k}" aria-pressed="${S.nyFilter === k}">${l} <b>${n}</b></button>`).join('')}</div>` : '';
  const handledLine = handled.length ? `<div class="ny-foot ny-handled"><button type="button" class="dv-link" data-ny-toggle="handled" aria-expanded="${S.showHandled}">${handled.length} handled ${S.showHandled ? '▴' : '▾'}</button>${S.showHandled
    ? handled.map((/** @type {any} */ i) => { const w = handledWords(i); return `<div class="ny-hrow"><span><b>${escHtml(i.name)}</b>: ${escHtml(w.what)}. ${escHtml(w.next)}</span><button type="button" class="dv-link" data-ny-undo="${escHtml(keyOf(i))}">Undo</button></div>`; }).join('') : ''}</div>` : '';
  const names = N.noApp.map((/** @type {any} */ p) => p.name);
  const list = names.length < 2 ? names.join('') : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
  const noApp = names.length ? `<div class="ny-foot"><button type="button" class="dv-link" data-ny-toggle="noapp" aria-expanded="${S.showNoApp}">${names.length} ${names.length === 1 ? 'client doesn\'t' : 'clients don\'t'} use the app ${S.showNoApp ? '▴' : '▾'}</button>${S.showNoApp
    ? `<p>${escHtml(list)}. They never show as not started. If their sessions and texts stop, they show as gone quiet.</p>` : ''}</div>` : '';
  return `<section class="dv-card ${wide ? 'dv-s12' : 'dv-s6'} ny" aria-labelledby="ny-h">
    <div class="dv-card-head"><h2 class="dv-title" id="ny-h">Needs you this week<span class="ny-count">${open.length}</span></h2></div>
    ${chips}
    ${rows.length ? rows.map(rowHtml).join('') : '<div class="ny-clear">Everyone is handled. New names show up here as they come.</div>'}
    ${handledLine}${noApp}
  </section>`;
}

/** The right-hand card's lists, each row saying what it is on its own ("Waiting 3 days", "No credit"). @param {any} W */
function waitingGroups(W) {
  const C = W.checkins, Ss = W.sessions, P = W.programs;
  /** @type {any[]} */
  const groups = [];
  const ci = C.items.map((/** @type {any} */ c) => ({ ...c, act: 'answer', label: 'Answer', ref: c.checkinId }));
  groups.push({ key: 'ci', label: 'Check-ins', items: ci, link: ['queue', 'Open the queue'],
    top: ci.length && C.total ? `<div class="cw-bar"><span class="tr" aria-hidden="true"><i style="width:${Math.round((C.answered / C.total) * 100)}%"></i></span><span>${C.answered} of ${C.total} check-ins answered</span></div>` : '' });
  const later = Ss.later || [];
  // ⚠️ A list that did not load says so (final review 10/20): never a confident "nothing waiting"
  const ssNote = Ss.failed ? 'Your sessions didn\'t load. They show again on the next try.' : Ss.creditsRead === false ? 'Session credits couldn\'t be checked right now, so sessions with no credit are not shown.' : '';
  groups.push({ key: 'ss', label: 'Sessions', link: ['calendar', 'Open calendar'], note: ssNote,
    items: Ss.items.map((/** @type {any} */ s) => ({ ...s, act: s.open === 'request' ? 'tracker' : 'session', label: 'Open', ref: s.sessionId || s.requestId })),
    foot: later.length ? `<button type="button" class="dv-link" data-ny-toggle="later-s" aria-expanded="${S.showLaterSessions}">${later.length} more ${later.length === 1 ? 'session' : 'sessions'} further out ${later.length === 1 ? 'has' : 'have'} no credit yet ${S.showLaterSessions ? '▴' : '▾'}</button>${S.showLaterSessions ? `<p>${later.map((/** @type {string} */ d) => escHtml(shortDay(d))).join(', ')}.</p>` : ''}` : '' });
  const pl = P.later || [];
  groups.push({ key: 'pg', label: 'Programs', link: null, note: P.failed ? 'Programs didn\'t load. They show again on the next try.' : '',
    items: P.items.map((/** @type {any} */ p) => ({ ...p, act: 'plan', label: 'Plan', ref: p.clientId })),
    foot: pl.length ? `<button type="button" class="dv-link" data-ny-toggle="later-p" aria-expanded="${S.showLaterPrograms}">${pl.length} more ${pl.length === 1 ? 'program or phase ends' : 'programs or phases end'} in the next 30 days ${S.showLaterPrograms ? '▴' : '▾'}</button>${S.showLaterPrograms ? `<p>${escHtml(pl.join(', '))}.</p>` : ''}` : '' });
  return groups;
}

/** @param {any} i */
const waitingRow = (i) => `<div class="ny-item">${avatar(i)}
    <div class="ny-who"><div class="ny-name"><span>${escHtml(i.name)}</span></div><div class="ny-detail" title="${escHtml(i.detail)}">${escHtml(i.detail)}</div></div>
    <span class="ny-tag ${i.tone === 'late' ? 'is-late' : i.tone === 'warn' ? 'is-warn' : ''}">${escHtml(i.tag)}</span>
    <div class="ny-acts"><button type="button" class="ny-b is-answer" data-ny-open="${i.act}" data-ny-ref="${escHtml(i.ref)}" data-ny-client="${escHtml(i.clientId)}" data-ny-at="${escHtml(i.at || '')}" aria-label="${escHtml(i.label)}: ${escHtml(i.name)}">${i.label}</button></div></div>`;

/** @param {any} W */
function waitingCard(W) {
  const groups = waitingGroups(W).filter((g) => g.items.length || g.note);
  if (!groups.length) return '';
  if (S.wFilter !== 'all' && !groups.some((g) => g.key === S.wFilter)) S.wFilter = 'all';
  const shown = S.wFilter === 'all' ? groups : groups.filter((g) => g.key === S.wFilter);
  const total = groups.reduce((n, g) => n + g.items.length, 0);
  const one = shown.length === 1 ? shown[0] : null;
  const chips = groups.length > 1 ? `<div class="ny-chips" role="group" aria-label="Show">${[['all', 'All', total], ...groups.map((g) => [g.key, g.label, g.items.length])]
    .map(([k, l, n]) => `<button type="button" class="ny-chip" data-ny-wfilter="${k}" aria-pressed="${S.wFilter === k}">${l} <b>${n}</b></button>`).join('')}</div>` : '';
  return `<section class="dv-card dv-s6 ny cw" aria-labelledby="wo-h">
    <div class="dv-card-head"><h2 class="dv-title" id="wo-h">Waiting on you<span class="ny-count">${total}</span></h2>${one && one.link ? `<button type="button" class="dv-link" data-ny-link="${one.link[0]}">${one.link[1]}${ICON.go}</button>` : ''}</div>
    ${chips}
    ${shown.map((g) => `${g.top || ''}${g.items.map(waitingRow).join('')}${g.note ? `<p class="ny-note" role="status">${escHtml(g.note)}</p>` : ''}`).join('')}
    ${shown.filter((g) => g.foot).map((g) => `<div class="ny-foot">${g.foot}</div>`).join('')}
  </section>`;
}

/** Draw the row into the dashboard's bottom slot (index.js puts it there). */
export function nyPaint() {
  const h = host();
  if (!h) return;
  if (!S.data) {
    h.innerHTML = S.failed ? `<section class="dv-card dv-s12 ny"><p class="ny-clear" role="alert">Needs you and Waiting on you didn't load. <button type="button" class="dv-link" data-ny-retry>Try again</button></p></section>` : '';
    return;
  }
  const side = waitingCard(S.data.waiting);
  const N = S.data.needsYou;
  const needs = N.items.length || N.handled.length || N.noApp.length || !side ? needsCard(N, !side) : '';
  // Keep the keyboard where it was: the row is drawn again after every choice
  const active = /** @type {HTMLElement|null} */ (document.activeElement);
  const focusSel = active && h.contains(active) ? focusKey(active) : null;
  h.innerHTML = side && !needs ? side.replace('dv-s6', 'dv-s12') : `${needs}${side}`;
  const next = S.focusNext; S.focusNext = null;
  const target = /** @type {HTMLElement|null} */ ((next && h.querySelector(next)) || (focusSel && h.querySelector(focusSel)));
  target?.focus();
}

/** A selector that finds the same button after a redraw. @param {HTMLElement} el */
function focusKey(el) {
  const row = el.closest('[data-ny-key]');
  for (const a of ['data-ny-filter', 'data-ny-wfilter', 'data-ny-toggle', 'data-ny-act', 'data-ny-reason', 'data-ny-weeks', 'data-ny-ref', 'data-ny-link', 'data-ny-undo']) {
    const v = el.getAttribute(a);
    if (v != null) return `${row ? `[data-ny-key="${q(row.getAttribute('data-ny-key') || '')}"] ` : ''}[${a}="${q(v)}"]`;
  }
  return null;
}

// ── What the coach picks ──

/** @param {string} key */
function findItem(key) {
  const N = S.data && S.data.needsYou;
  if (!N) return null;
  const all = [...N.items, ...N.handled];
  return all.find((i) => keyOf(i) === key) || null;
}

/**
 * Save a choice: the page shows it at once, the server keeps it, and the lists load again.
 * @param {any} item @param {{ action: string|null, reason?: string|null, weeks?: number|null, worried: boolean, actedAt?: string|null, checkBackOn?: string|null }} c
 * @param {any} [undoTo] the row as it was, to put back if the save fails
 */
async function send(item, c, undoTo) {
  try {
    await callFunction('coach-needs-you', { clientId: item.clientId, kind: item.choiceKind, basis: item.basis, action: c.action,
      reason: c.reason || null, weeks: c.weeks || null, worried: c.worried, actedAt: c.actedAt || null, checkBackOn: c.checkBackOn || null }, 'POST');
    void load();
    return true;
  } catch (err) {
    if (undoTo) place(undoTo);
    nyPaint();
    showToast(`That didn't save: ${err instanceof Error ? err.message : err}`, 'error');
    return false;
  }
}

/** Put a row back as given, open or handled. @param {any} item */
function place(item) {
  S.dirtyAt = Date.now();
  const N = S.data.needsYou, k = keyOf(item);
  N.items = N.items.filter((/** @type {any} */ i) => keyOf(i) !== k);
  N.handled = N.handled.filter((/** @type {any} */ i) => keyOf(i) !== k);
  (item.handled ? N.handled : N.items).push(item);
  const order = { new: 0, quiet: 1, checkin: 2, eye: 3 };
  N.items.sort((/** @type {any} */ a, /** @type {any} */ b) => Number(b.worried) - Number(a.worried) || /** @type {any} */ (order)[a.kind] - /** @type {any} */ (order)[b.kind]);
}

/** Done, or Not needed with its reason: the row leaves, one toast says so with Undo. @param {any} item @param {string} action @param {string|null} [reason] @param {number|null} [weeks] */
async function handle(item, action, reason = null, weeks = null) {
  const before = { ...item };
  const checkBackOn = weeks ? addWeeks(S.data.today, weeks) : null;
  const after = { ...item, handled: { action, reason, checkBackOn, at: new Date().toISOString() } };
  S.asking = null; S.menuFor = null;
  place(after); nyPaint();
  if (!await send(after, { action, reason, weeks, worried: !!item.worried }, before)) return;
  const w = handledWords(after);
  showToast(`${item.name}: ${w.what}. ${w.next}`, 'success', { actionLabel: 'Undo', onAction: () => { void undo(after, before); } });
}

/** @param {any} now the row as it is @param {any} back the row as it was */
async function undo(now, back) {
  place(back); nyPaint();
  await send(back, { action: null, worried: !!back.worried }, now);
}

/** @param {string} day @param {number} weeks */
function addWeeks(day, weeks) {
  const t = new Date(`${day}T00:00:00Z`); t.setUTCDate(t.getUTCDate() + weeks * 7);
  return t.toISOString().slice(0, 10);
}

/** @param {HTMLElement} t */
async function openThing(t) {
  const what = t.dataset.nyOpen, ref = t.dataset.nyRef || '', clientId = t.dataset.nyClient || '';
  const w = /** @type {any} */ (window);
  if (what === 'plan') { if (typeof w.showClientProfile === 'function') w.showClientProfile(clientId); return; }
  if (what === 'answer') {
    showPage('checkins');
    try {
      const m = await import('../checkin-review.js');
      // ⚠️ Only a check-in the page has loaded: openCheckinView keeps an unknown id and stops repainting the queue
      if (m.getCheckins().some((/** @type {any} */ c) => c.id === ref)) setTimeout(() => { void m.openCheckinView(ref); }, 120);
    } catch (err) { console.warn('[dashboard] the check-in did not open:', err instanceof Error ? err.message : err); }
    return;
  }
  if (what === 'tracker') {
    try { const m = await import('../notification-bell.js'); await m.landOnSessionTracker(clientId); }
    catch { if (typeof w.showClientProfile === 'function') w.showClientProfile(clientId); }
    return;
  }
  if (what === 'session') await openSession(t, ref, t.dataset.nyAt || '');
}

/** The calendar's own peek for a session on any day, here on the dashboard. @param {HTMLElement} anchorEl @param {string} id @param {string} at */
async function openSession(anchorEl, id, at) {
  try {
    const [cal, days, fmt] = await Promise.all([import('../coach-calendar/index.js'), import('../coach-calendar/day-data.js'), import('../coach-calendar/format.js')]);
    if (!S.unlisten) S.unlisten = cal.onCalendarChanged(() => { if (showing()) void load(); else S.loadedAt = 0; });
    const d = fmt.dateInZone(Date.parse(at));
    // Whole UTC days on the server: the day either side makes sure the session's own day is complete (today.js)
    const [{ byDate, cancelledByDate, failed }, creditBalances] = await Promise.all([days.loadSessions(fmt.addDays(d, -1), fmt.addDays(d, 1)), days.fetchCreditBalances([anchorEl.dataset.nyClient || ''])]);
    if (failed) throw new Error('the day did not load');
    if (!(byDate[d] || []).some((/** @type {any} */ s) => s.id === id)) { void load(); return; } // dealt with elsewhere meanwhile
    // The row may have been drawn again while the day loaded: the peek sits beside the new button
    const anchor = anchorEl.isConnected ? anchorEl : /** @type {HTMLElement|null} */ (host()?.querySelector(`[data-ny-open="session"][data-ny-ref="${q(id)}"]`)) || anchorEl;
    await cal.openPeekFromDay({ kind: 'session', id, anchorEl: anchor, fresh: true, day: { dateISO: d, sessions: byDate[d] || [], cancelled: cancelledByDate[d] || [], creditBalances } });
  } catch (err) {
    console.warn('[dashboard] the session did not open here:', err instanceof Error ? err.message : err);
    showPage('calendar');
  }
}

/** @param {string} name */
function showPage(name) {
  const nav = /** @type {any} */ (window)._navigation;
  if (nav && typeof nav.showView === 'function') nav.showView(name);
  else /** @type {HTMLElement|null} */ (document.querySelector(`.nav-btn[data-view="${name}"]`))?.click();
}

/** @param {MouseEvent} e */
function onClick(e) {
  const t = /** @type {HTMLElement|null} */ (e.target instanceof Element ? e.target.closest('#dv-root .ny-row button') : null);
  if (!t) {
    // A click anywhere else closes an open ⋯ menu
    if (S.menuFor && !(e.target instanceof Element && e.target.closest('#dv-root .ny-acts'))) { S.menuFor = null; nyPaint(); }
    return;
  }
  const ds = t.dataset;
  if (ds.nyRetry !== undefined) { S.failed = false; nyPaint(); void load(); return; }
  if (ds.nyFilter) { S.nyFilter = ds.nyFilter; nyPaint(); return; }
  if (ds.nyWfilter) { S.wFilter = ds.nyWfilter; nyPaint(); return; }
  if (ds.nyToggle) {
    if (ds.nyToggle === 'noapp') S.showNoApp = !S.showNoApp;
    else if (ds.nyToggle === 'handled') S.showHandled = !S.showHandled;
    else if (ds.nyToggle === 'later-s') S.showLaterSessions = !S.showLaterSessions;
    else S.showLaterPrograms = !S.showLaterPrograms;
    nyPaint(); return;
  }
  if (ds.nyLink) { showPage(ds.nyLink === 'queue' ? 'checkins' : 'calendar'); return; }
  if (ds.nyOpen) { void openThing(t); return; }
  if (ds.nyUndo) { const i = findItem(ds.nyUndo); if (i && i.handled) void undo(i, { ...i, handled: undefined }); return; }
  const row = /** @type {HTMLElement|null} */ (t.closest('[data-ny-key]'));
  const item = row ? findItem(row.dataset.nyKey || '') : null;
  if (!item) return;
  const k = keyOf(item);
  if (ds.nyReason) {
    if (ds.nyReason === 'trip' || ds.nyReason === 'sick') { S.asking = { key: k, why: ds.nyReason }; S.focusNext = `[data-ny-key="${q(k)}"] [data-ny-weeks="1"]`; nyPaint(); return; }
    void handle(item, 'not_needed', ds.nyReason); return;
  }
  if (ds.nyWeeks) { void handle(item, 'not_needed', S.asking && S.asking.why, Number(ds.nyWeeks)); return; }
  const act = ds.nyAct;
  if (act === 'more') {
    S.menuFor = S.menuFor === k ? null : k; S.asking = null;
    if (S.menuFor) S.focusNext = `[data-ny-key="${q(k)}"] .ny-menu [role="menuitem"]`;
    nyPaint(); return;
  }
  if (act === 'msg') { void message(item); return; }
  if (act === 'done') { void handle(item, 'done'); return; }
  if (act === 'not') { S.menuFor = null; S.asking = { key: k, why: null }; S.focusNext = `[data-ny-key="${q(k)}"] [data-ny-reason]`; nyPaint(); return; }
  if (act === 'worry') {
    S.menuFor = null; S.focusNext = `[data-ny-key="${q(k)}"] [data-ny-act="more"]`;
    const before = { ...item }, after = { ...item, worried: !item.worried };
    place(after); nyPaint();
    void send(after, { action: null, worried: after.worried }, before);
  }
}

/**
 * Message opens the corner chat with that client, like everywhere else in the CRM. When he sends from it, the name
 * moves to handled as Messaged (the prototype's rule), with the same toast and Undo.
 * ⚠️ HIS SEND, NOT ANY MESSAGE (final review 10/20): the app's own automatic messages are stored just like his, so
 * the card listens, from the press, for his next message to that client in that chat (the live feed every
 * message comes in on).
 * @param {any} item
 */
async function message(item) {
  const w = /** @type {any} */ (window);
  S.chatFor = { key: keyOf(item), at: new Date(Date.now() - 1000).toISOString() };
  void listenForSend();
  if (w._cornerMessages && typeof w._cornerMessages.open === 'function') { w._cornerMessages.open({ clientId: item.clientId }); return; }
  try { const m = await import('../corner-messages/index.js'); m.openCornerChat({ clientId: item.clientId }); }
  catch (err) { console.warn('[dashboard] the chat did not open:', err instanceof Error ? err.message : err); }
}

async function listenForSend() {
  if (S.unfeed) return;
  try {
    const feed = await import('../messages/message-feed.js');
    S.unfeed = feed.subscribe((/** @type {any} */ row) => {
      const c = S.chatFor;
      if (!c || !row || row.sender_role !== 'coach' || row.group_id || row.lead_id || (row.source && row.source !== 'manual')) return;
      const item = findItem(c.key);
      if (!item || item.clientId !== row.client_id || item.handled || String(row.created_at || '') < c.at) return;
      S.chatFor = null;
      void handle(item, 'messaged');
    });
  } catch (err) { console.warn('[dashboard] Needs you cannot hear sends:', err instanceof Error ? err.message : err); }
}

/** @param {KeyboardEvent} e */
function onKey(e) {
  if (e.key !== 'Escape' || (!S.menuFor && !S.asking)) return;
  const k = S.menuFor || (S.asking && S.asking.key);
  S.menuFor = null; S.asking = null; nyPaint();
  /** @type {HTMLElement|null} */ (host()?.querySelector(`[data-ny-key="${q(k || '')}"] [data-ny-act="more"]`))?.focus();
}

/** For tests: forget everything and stop listening. */
export function __resetNeedsYouForTests() {
  if (S.tick) clearTimeout(S.tick);
  document.removeEventListener('click', onClick);
  document.removeEventListener('keydown', onKey);
  S.unlisten?.(); S.unfeed?.();
  Object.assign(S, { data: null, loading: null, loadedAt: 0, failed: false, started: false, tick: null, nyFilter: 'all', wFilter: 'all',
    showNoApp: false, showHandled: false, showLaterSessions: false, showLaterPrograms: false, menuFor: null, asking: null, unlisten: null,
    again: false, dirtyAt: 0, focusNext: null, chatFor: null, unfeed: null });
}
